import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isApiError } from "@bw/shared/api";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import type { SavedPaymentMethodResponse } from "@bw/shared/types/generated";
import {
  isDefinitivePaymentMethodRemovalFailure,
  isSamePaymentMethodIdentity,
  paymentMethodIdentityKey,
  useWalletSetupIntent,
} from "@bw/shared/hooks";
import {
  formatSavedPaymentMethodExpiry,
  getSavedPaymentMethodUnavailableLabel,
} from "@bw/shared/utils";
import { Page } from "../../../common/components/Page";
import { DetailHeader } from "../../../common/components/DetailHeader";
import { EmptyState, ErrorCard, ListSkeleton } from "../../../common/components/states";
import CreditCardRounded from "@mui/icons-material/CreditCardRounded";
import { MaterialAddCardForm } from "../../../common/components/payment";
import {
  useSavedPaymentMethods,
  useRemovePaymentMethod,
} from "../hooks/useSavedPaymentMethods";

/**
 * PaymentMethodsPage (/me/payments) — the player's single card wallet.
 *
 * Reads the caller's saved cards via useSavedPaymentMethods (GET
 * /users/me/payment-methods, SERVER-SCOPED — no uid is ever passed). The server
 * returns canonical, player-owned cards. A facility clone is an internal
 * payment-routing detail and is deliberately neither grouped nor exposed here.
 *
 * Each row shows brand, the masked
 * "•••• {last4}", and a validated expiry "MM/YYYY" (or an honest unavailable
 * label for malformed provider data); the default card carries a "Default" badge.
 *
 * Honesty: the opaque keys (connect_account_id / org_id / composite id /
 * payment_method_id) are NEVER visible text — the composite `id` is used ONLY as
 * a React key and as the mutation argument. Removal requires confirmation and
 * fires once. Canonical wallet caches become unchargeable before dispatch; this
 * page keeps a disabled row snapshot until the outcome is known, then refetches
 * server truth after a confirmed detach.
 *
 * The AppShell supplies no page title, so this routed page OWNS its single <h1>
 * via <Page title="Payment methods">; the content does NOT repeat that title.
 */

type RemovalStatus = "pending" | "definitive_error" | "uncertain";

interface RemovalSnapshot {
  card: SavedPaymentMethodResponse;
  index: number;
  status: RemovalStatus;
  message?: string;
}

export function PaymentMethodsPage() {
  const queryClient = useQueryClient();
  const methods = useSavedPaymentMethods();
  const remove = useRemovePaymentMethod();
  const setup = useWalletSetupIntent();
  const [addingCard, setAddingCard] = useState(false);
  const [cardSetupBusy, setCardSetupBusy] = useState(false);
  const [removal, setRemoval] = useState<RemovalSnapshot | null>(null);
  // React Query publishes mutation state on the next render. This immediate
  // guard serializes all wallet removals, including two confirms in one tick.
  const removalFiredRef = useRef(false);

  async function handleRemove(card: SavedPaymentMethodResponse, index: number) {
    if (removalFiredRef.current) return;
    removalFiredRef.current = true;
    const snapshot: RemovalSnapshot = {
      card,
      index: Math.max(0, index),
      status: "pending",
    };
    // The canonical cache is filtered before DELETE dispatch. Keep a disabled
    // presentation snapshot in this routed parent so the user still sees the
    // in-flight/uncertain row without making it chargeable anywhere else.
    setRemoval(snapshot);
    try {
      await remove.mutateAsync(card);
      setRemoval(null);
    } catch (error) {
      setRemoval({
        ...snapshot,
        message:
          isApiError(error) && error.code === "PAYMENT_METHOD_IN_USE"
            ? error.message
            : undefined,
        status: isDefinitivePaymentMethodRemovalFailure(error)
          ? "definitive_error"
          : "uncertain",
      });
    } finally {
      removalFiredRef.current = false;
    }
  }

  const cards = methods.data ?? [];
  let displayCards = cards;
  if (
    removal !== null &&
    !cards.some((card) => isSamePaymentMethodIdentity(card, removal.card))
  ) {
    displayCards = [...cards];
    displayCards.splice(
      Math.min(removal.index, displayCards.length),
      0,
      removal.card,
    );
  }

  // An empty wallet's one action is the empty state's Add card.
  const emptyWallet = methods.isSuccess && displayCards.length === 0;

  return (
    <Page narrow>
      <Stack spacing={2}>
        <DetailHeader
          back={{ to: "/me/account", label: "Back to account" }}
          title="Saved cards"
          subtitle="Use your saved cards across facilities."
          wrapSubtitle
          actions={
          emptyWallet ? undefined : (
          <Button
            variant="contained"
            size="small"
            sx={{ flexShrink: 0, borderRadius: 999 }}
            onClick={() => setAddingCard(true)}
            disabled={addingCard || removal?.status === "pending"}
            aria-expanded={addingCard}
            aria-controls="account-add-card"
          >
            Add card
          </Button>
          )}
        />
        {addingCard ? (
          <Stack id="account-add-card" spacing={2} sx={{ maxWidth: 560 }}>
            <MaterialAddCardForm
              autoFocus
              createSetupIntent={async (key) =>
                (await setup.mutateAsync(key)).client_secret
              }
              onBusyChange={setCardSetupBusy}
              onCardAdded={async (reference) => {
                const refreshed = await methods.refetch();
                if (
                  refreshed.isError ||
                  !refreshed.data?.some(
                    (card) =>
                      card.payment_method_id === reference && card.is_usable,
                  )
                ) {
                  throw new Error(
                    "The saved card is not available yet. Please retry the refresh.",
                  );
                }
                await queryClient.invalidateQueries({
                  queryKey: ["booking", "payment-methods"],
                });
                setAddingCard(false);
              }}
            />
            <Button
              variant="text"
              size="small"
              sx={{ alignSelf: "flex-start" }}
              disabled={cardSetupBusy}
              onClick={() => setAddingCard(false)}
            >
              Cancel
            </Button>
          </Stack>
        ) : null}
        {methods.isLoading ? (
          <ListSkeleton label="Loading your saved cards…" leading="square" />
        ) : methods.isError ? (
          <ErrorCard
            title="We couldn't load your saved cards. Please try again."
            onRetry={() => void methods.refetch()}
          />
        ) : displayCards.length === 0 ? (
          addingCard ? null : (
            <EmptyState
              icon={<CreditCardRounded />}
              title="No saved cards yet"
              body="Add a card once and use it at any facility."
              action={{ label: "Add card", onClick: () => setAddingCard(true) }}
            />
          )
        ) : (
          <List
            disablePadding
            aria-label="Saved cards"
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            {displayCards.map((card) => (
              <PaymentMethodRow
                key={card.id ?? paymentMethodIdentityKey(card)}
                card={card}
                removalStatus={
                  removal !== null &&
                  isSamePaymentMethodIdentity(removal.card, card)
                    ? removal.status
                    : null
                }
                removalMessage={removal?.message}
                removalGloballyPending={
                  removal?.status === "pending" || cardSetupBusy
                }
                onRemove={() =>
                  handleRemove(
                    card,
                    displayCards.findIndex((candidate) =>
                      isSamePaymentMethodIdentity(candidate, card),
                    ),
                  )
                }
              />
            ))}
          </List>
        )}
      </Stack>
    </Page>
  );
}

/**
 * One saved-card row: brand · "•••• last4" · expiry, an optional "Default" badge,
 * and a remove control gated behind an inline confirm panel (mirrors the
 * privacy-page destructive-confirm idiom — a confirm + cancel, no one-click
 * delete). The mutation fires with the card's opaque wallet reference exactly once.
 */
function PaymentMethodRow({
  card,
  removalStatus,
  removalGloballyPending,
  removalMessage,
  onRemove,
}: {
  card: SavedPaymentMethodResponse;
  removalStatus: RemovalStatus | null;
  removalGloballyPending: boolean;
  removalMessage?: string;
  onRemove: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  const unavailableLabel = getSavedPaymentMethodUnavailableLabel(
    card.unavailable_reason,
  );
  const expiry = formatSavedPaymentMethodExpiry(card.exp_month, card.exp_year);
  const rowPending = removalStatus === "pending";
  const removalOutcomeUncertain = removalStatus === "uncertain";
  const removalFailedDefinitively = removalStatus === "definitive_error";

  function handleConfirm() {
    if (rowPending || removalOutcomeUncertain) return;
    onRemove();
  }

  return (
    <ListItem
      component="li"
      divider
      sx={{ display: "block", px: { xs: 2, sm: 2.5 }, py: 2 }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
      >
        <Box>
          <Typography
            component="span"
            sx={{ fontWeight: 700, textTransform: "capitalize" }}
          >
            {card.brand}
          </Typography>{" "}
          <Typography component="span" color="text.secondary">
            •••• {card.last4}
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", flexWrap: "wrap" }}
        >
          {card.is_default ? (
            <Chip label="Default" size="small" color="primary" />
          ) : null}
          <Typography component="span" variant="body2" color="text.secondary">
            {expiry ? `Expires ${expiry}` : "Expiry unavailable"}
          </Typography>
          {!card.is_usable && unavailableLabel ? (
            <Chip label={unavailableLabel} size="small" color="error" />
          ) : null}
        </Stack>
      </Stack>

      <Divider sx={{ my: 1.5 }} />
      <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
        {!confirming ? (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setConfirming(true)}
            disabled={removalGloballyPending || removalOutcomeUncertain}
          >
            Remove
          </Button>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={handleConfirm}
              disabled={removalGloballyPending || removalOutcomeUncertain}
            >
              {rowPending ? "Removing…" : "Confirm remove"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setConfirming(false)}
              disabled={removalGloballyPending || removalOutcomeUncertain}
            >
              Cancel
            </Button>
          </Stack>
        )}
        {removalFailedDefinitively || removalOutcomeUncertain ? (
          <Alert severity="error">
            {removalOutcomeUncertain
              ? "We couldn't verify whether this card was removed. For safety, use another card."
              : (removalMessage ??
                "We couldn't remove this card. Please try again.")}
          </Alert>
        ) : null}
      </Stack>
    </ListItem>
  );
}
